export const SX_BASE = '/sx-business/decouvrir';
export const SX_PROJECT = '/sx-business#configurateur';
export const SX_HERO = '/assets/sx/sx-service-hero.webp';
export const SX_CALENDAR = 'https://calendly.com/gourpil-rdv-partners/services-et-technologies-by-rdv';
export const SX_DESCRIPTION = 'Faites de votre service une expérience qui donne envie de choisir, de rester et de revenir. SX Business relie votre proposition de valeur, le parcours client et votre organisation.';

type ServiceSituation = {
  quote: string;
  status: string;
  screenTitle: string;
  screenItems: string[];
  evidence: string;
  team: string[];
  outcomeTitle: string;
  outcome: string;
};

type ServiceStep = {
  label: string;
  phase: string;
  focus: string;
  question: string;
  confused: ServiceSituation;
  clarified: ServiceSituation;
};

export const SERVICE_STEPS: ServiceStep[] = [
  {
    label: 'Décider', phase: 'Avant l’engagement', focus: 'Choisir une offre adaptée',
    question: 'Est-ce le bon service pour mon besoin ?',
    confused: {
      quote: 'Je compare les offres, mais je ne vois pas laquelle répond à ma situation ni ce que j’obtiendrai vraiment.',
      status: 'Une valeur difficile à percevoir',
      screenTitle: 'Une offre qui laisse hésiter',
      screenItems: ['Des prestations sans lien clair avec mon besoin', 'Un contenu et des limites difficiles à comparer', 'Des engagements trop vagues pour me décider'],
      evidence: 'Le prix devient le principal repère de comparaison, faute de comprendre la valeur du service.',
      team: ['Chaque commercial décrit l’offre à sa façon.', 'Les demandes arrivent sans qualification des attentes.'],
      outcomeTitle: 'Une décision qui se bloque',
      outcome: 'Hésitations, contacts peu qualifiés et projets abandonnés avant l’engagement.',
    },
    clarified: {
      quote: 'Je reconnais mon besoin dans l’offre. Je sais ce qui est inclus, ce que le service m’apportera et à quelles conditions.',
      status: 'Je choisis en confiance',
      screenTitle: 'Une offre à la mesure du besoin',
      screenItems: ['Des prestations reliées au résultat que je recherche', 'Un périmètre, des options et un prix expliqués', 'Des engagements concrets pour évaluer la proposition'],
      evidence: 'La proposition donne au client des repères pour apprécier la pertinence du service avant de s’engager.',
      team: ['Associer les attentes du client à la bonne composition de service.', 'Présenter une offre et des engagements partagés par tous.'],
      outcomeTitle: 'Une offre choisie pour sa valeur',
      outcome: 'Le client identifie la réponse à son besoin ; l’entreprise concentre ses échanges sur les projets qui lui correspondent.',
    },
  },
  {
    label: 'Démarrer', phase: 'Après l’accord', focus: 'Mettre le service en route',
    question: 'Comment bien commencer ma prestation ?',
    confused: {
      quote: 'J’ai accepté, mais personne ne m’explique comment commencer. On me redemande des informations déjà transmises.',
      status: 'Je reste dans l’attente',
      screenTitle: 'Un lancement sans mode d’emploi',
      screenItems: ['Aucun interlocuteur de lancement identifié', 'Des pièces demandées en plusieurs fois', 'Des accès et une date de démarrage incertains'],
      evidence: 'L’accord est donné, mais le client doit encore comprendre seul comment activer le service.',
      team: ['La vente transmet un dossier incomplet à la réalisation.', 'Les accès et les préparatifs sont traités au dernier moment.'],
      outcomeTitle: 'Un démarrage qui s’enlise',
      outcome: 'Temps perdu à reconstituer le dossier, lancement repoussé et confiance fragilisée dès l’accueil.',
    },
    clarified: {
      quote: 'Je suis attendu. Mon interlocuteur connaît déjà mon dossier et je dispose de tout ce qu’il faut pour commencer.',
      status: 'Je suis prêt à commencer',
      screenTitle: 'Un accueil qui met en action',
      screenItems: ['Un référent et un rendez-vous de lancement confirmés', 'Une liste unique des éléments à préparer', 'Mes accès et mes premières consignes disponibles'],
      evidence: 'L’accueil est adapté à la prestation choisie : le client sait quoi préparer, où le déposer et avec qui démarrer.',
      team: ['Transmettre le contexte et le périmètre validé au responsable du dossier.', 'Préparer les accès, les ressources et le premier rendez-vous.'],
      outcomeTitle: 'Un service rapidement opérationnel',
      outcome: 'Le client entre dans l’usage ; l’équipe dispose d’un dossier complet et des moyens prêts pour engager la prestation.',
    },
  },
  {
    label: 'Avancer', phase: 'Pendant la réalisation', focus: 'Tenir les engagements du service',
    question: 'Ma prestation avance-t-elle comme prévu ?',
    confused: {
      quote: 'Je dois relancer pour savoir où en est le travail. Je découvre les changements et leurs conséquences trop tard.',
      status: 'Je perds la visibilité',
      screenTitle: 'Une réalisation difficile à suivre',
      screenItems: ['Un avancement absent des échanges', 'Des validations attendues sans échéance claire', 'Des modifications réalisées sans accord partagé'],
      evidence: 'Le client subit les écarts de délai ou de périmètre au lieu de pouvoir prendre les décisions à temps.',
      team: ['Les tâches et les échéances restent dans les outils de chacun.', 'Les changements se perdent dans les messages et les versions.'],
      outcomeTitle: 'Une prestation sous tension',
      outcome: 'Relances, retards, reprises et désaccords sur ce qui doit être livré.',
    },
    clarified: {
      quote: 'Je vois les étapes réalisées et la prochaine échéance. Je peux donner mon avis et valider chaque ajustement en connaissance de cause.',
      status: 'Je garde la maîtrise',
      screenTitle: 'Une réalisation coordonnée',
      screenItems: ['Un suivi des livrables et des jalons convenus', 'Les décisions attendues avec leur date de validation', 'Les effets de chaque changement soumis à mon accord'],
      evidence: 'Le suivi relie les travaux réalisés, les décisions du client et les engagements de l’équipe.',
      team: ['Planifier les tâches, affecter les ressources et suivre les échéances.', 'Contrôler la qualité et tracer les validations du client.'],
      outcomeTitle: 'Des engagements tenus ensemble',
      outcome: 'Le client participe aux bonnes décisions au bon moment ; l’équipe maîtrise la réalisation, la qualité et les délais convenus.',
    },
  },
  {
    label: 'Revenir', phase: 'Après la prestation', focus: 'Prolonger la valeur du service',
    question: 'Comment tirer durablement parti du service ?',
    confused: {
      quote: 'Tout s’arrête à la livraison. Je ne sais pas où trouver de l’aide ni comment continuer à utiliser ce qui a été mis en place.',
      status: 'Je me retrouve seul après la livraison',
      screenTitle: 'Une relation qui s’interrompt',
      screenItems: ['Des ressources remises sans accompagnement à l’usage', 'Aucun point de suivi pour faire le bilan', 'Une relance commerciale sans lien avec mes besoins'],
      evidence: 'La fin de la prestation ne prépare ni l’autonomie du client, ni une éventuelle suite pertinente.',
      team: ['Les retours d’expérience ne sont ni recueillis ni exploités.', 'Le suivi dépend d’une relance ponctuelle, sans bilan d’usage.'],
      outcomeTitle: 'Une valeur qui s’essouffle',
      outcome: 'Le service est peu exploité dans la durée ; la relation se distend et les occasions de fidélisation se perdent.',
    },
    clarified: {
      quote: 'Je sais utiliser les ressources livrées et obtenir de l’aide. Le bilan m’aide à voir les progrès et à choisir une suite vraiment utile.',
      status: 'Je continue à en tirer de la valeur',
      screenTitle: 'Une continuité pensée dès la livraison',
      screenItems: ['Des ressources et une aide accessibles après la prestation', 'Un bilan des usages, des acquis et des besoins restants', 'Une suite proposée selon mes nouvelles priorités'],
      evidence: 'La relation s’appuie sur l’expérience vécue pour consolider les acquis et identifier un prochain besoin réel.',
      team: ['Organiser le suivi d’usage et intégrer les retours au service.', 'Préparer des recommandations adaptées au bilan du client.'],
      outcomeTitle: 'Une relation qui se prolonge par l’utilité',
      outcome: 'Le client conserve les bénéfices du service ; l’entreprise construit des renouvellements pertinents et une relation propice à la recommandation.',
    },
  },
];

export const DELIVERABLES = [
  { number: '01', title: 'Une promesse précise', text: 'Pour qui, pour quel besoin, avec quel résultat recherché et quelles limites.', tag: 'Votre offre devient compréhensible' },
  { number: '02', title: 'Un parcours dessiné', text: 'Les étapes, les points de contact et les moments où le client a besoin d’être rassuré.', tag: 'Votre expérience prend forme' },
  { number: '03', title: 'Une organisation prête', text: 'Les rôles, les passages de relais, les messages et les outils nécessaires à chaque étape.', tag: 'Vos équipes savent quoi faire' },
  { number: '04', title: 'Un premier parcours testé', text: 'Un prototype ou un pilote confronté aux usages, puis un plan d’amélioration priorisé.', tag: 'Vous décidez à partir du terrain' },
];

export const FAQS = [
  { question: 'Le design de service, est-ce refaire mon site ?', answer: 'Il porte sur l’ensemble de la relation : comprendre l’offre, acheter, démarrer, recevoir le service et être accompagné. Un site peut en être un point de contact. Le travail concerne aussi les échanges humains, les documents et l’organisation de vos équipes.' },
  { question: 'Peut-on améliorer un service qui existe déjà ?', answer: 'Oui. Nous pouvons partir d’un moment précis : des prospects qui hésitent, un démarrage compliqué, trop de relances ou un renouvellement difficile. Le périmètre se construit autour de ce problème et des moyens disponibles.' },
  { question: 'Faut-il changer tous nos outils ?', answer: 'Le parcours cible permet d’identifier ce qui manque réellement. Les outils en place peuvent être conservés. Une connexion, un espace dédié ou une automatisation se justifient par un usage précis, défini avec vous.' },
  { question: 'Comment évaluer la valeur de la démarche ?', answer: 'Nous choisissons quelques indicateurs liés au problème de départ et relevons une situation initiale : délais, abandons, demandes d’aide, reprises, satisfaction ou renouvellement. Les observations du pilote permettent ensuite de comparer et d’ajuster, sans promettre un résultat chiffré à l’avance.' },
  { question: 'Comment définir mon projet SX Business ?', answer: 'Le configurateur vous aide à exprimer votre priorité et à composer un premier périmètre. L’échange commercial précise les livrables, l’accompagnement et les éventuels outils avant l’établissement de la proposition.' },
];
